import { ChangeDetectionStrategy, Component, DestroyRef, DestroyableInjector, ElementRef, Injector, TemplateRef, ViewEncapsulation, afterEveryRender, afterNextRender, computed, contentChildren, effect, forwardRef, inject, input, output, signal, untracked, viewChildren } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule, DOCUMENT, NgComponentOutlet } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent } from 'rxjs';
import { ICommand } from '@workbuddy/ui/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { SettingsPanelMenuItem, SettingsPanelMode, SettingsPanelNavigateItem, SettingsPanelEntityPickerItem, SettingsPanelNavigationService, SettingsPanelNavigateOptions, SettingsPanelDismissEvent, SettingsPanelEntry, SettingsPanelComponentEntry, SettingsPanelConfig, SettingsPanelSplitButton } from './model';
import { SettingsPanelForm } from './settings.panel.form';
import { SettingsPanelRendererDirective } from './renderer/settings.panel.renderer.directive';
import { SettingsPanelPageComponent } from './page/settings.panel.page.component';
import { SettingsPanelMenuComponent } from './menu/settings.panel.menu.component';
import { SettingsPanelPickerComponent } from './picker/settings.panel.picker.component';
import { SettingsPanelEntityPickerComponent } from './entity-picker/settings.panel.entity.picker.component';
import { settingsPanelBridgeStyles, settingsPanelStyleId } from './settings.panel.theme';
import { tonePickerBridgeStyles, tonePickerStyleId } from './menu/tone.picker.theme';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';

export { SettingsPanelMode };

@Component({
  selector: 'wb-settings-panel',
  templateUrl: './settings.panel.component.html',
  styleUrls: ['./settings.panel.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, A11yModule, SettingsPanelPageComponent, SettingsPanelMenuComponent, SettingsPanelPickerComponent, SettingsPanelEntityPickerComponent, NgComponentOutlet],
  providers: [{ provide: SettingsPanelNavigationService, useExisting: SettingsPanelComponent }, { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SettingsPanelComponent), multi: true }],
  host: {
    '[class.settings-panel-mode-command-palette]': '$mode() === SettingsPanelMode.CommandPalette',
    '[class.settings-panel-is-navigated]': '$panelIndex() > 0',
    '(keydown.escape)': 'onEscape($event)',
    '(keydown.backspace)': 'onBackspace($event)',
  },
})
export class SettingsPanelComponent extends SettingsPanelNavigationService implements ControlValueAccessor {

  protected readonly SettingsPanelMode = SettingsPanelMode;

  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmService = inject(ConfirmServicePro);
  private readonly injector = inject(Injector);
  private readonly entryInjectors = new Map<SettingsPanelComponentEntry, DestroyableInjector>();

  public $panel = input.required<SettingsPanelForm>({ alias: 'panel' });
  public $mode = input<SettingsPanelMode>(SettingsPanelMode.Sidebar, { alias: 'mode' });
  public $dismissOnClickOutside = input<boolean>(true, { alias: 'dismissOnClickOutside' });
  public $confirmDiscardChanges = input<boolean>(false, { alias: 'confirmDiscardChanges' });
  public $discardChangesTitle = input<string>('Discard changes?', { alias: 'discardChangesTitle' });
  public $discardChangesMessage = input<string>('You have unsaved changes. Are you sure you want to discard them?', { alias: 'discardChangesMessage' });

  public closed = output<void>();
  public clickOutside = output<SettingsPanelDismissEvent>();
  public itemClicked = output<SettingsPanelMenuItem>();
  public valueChange = output<Record<string, unknown>>();

  protected $config = computed(() => this.$panel().config);
  protected $formGroup = computed(() => this.$panel().formGroup ?? null);
  protected $isCommandPaletteMode = computed(() => this.$mode() === SettingsPanelMode.CommandPalette);
  protected $footerSplitButton = computed<SettingsPanelSplitButton | undefined>(() => this.withFormValidity(this.$config().footerSplitButton));

  private rendererDirectives = contentChildren(SettingsPanelRendererDirective);
  private componentOutlets = viewChildren(NgComponentOutlet);

  protected $activePageHeight = signal<number | null>(null);

  private activePageResizeObserver?: ResizeObserver;
  private observedActivePage: HTMLElement | null = null;

  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};

  public constructor() {

    super();
    DesignSystem.Mount(this.document, settingsPanelStyleId, settingsPanelBridgeStyles);
    DesignSystem.Mount(this.document, tonePickerStyleId, tonePickerBridgeStyles);
    this.setupClickOutside();
    this.setupValueChangeEmitter();
    afterNextRender(() => this.refreshActivePageHeightObserver());
    afterEveryRender(() => this.refreshActivePageHeightObserver());
    this.destroyRef.onDestroy(() => {

      this.entryInjectors.forEach(i => i.destroy());
      this.activePageResizeObserver?.disconnect();
    });

    // When the consumer hands us a new form (e.g. setup → configured stage swap),
    // focus the first focusable element in the now-active page.
    let lastPanel: SettingsPanelForm | null = null;
    effect(() => {
      const panel = this.$panel();
      const isFirst = lastPanel === null;
      const changed = !isFirst && panel !== lastPanel;
      lastPanel = panel;
      // Skip first read (initial render); only refocus on subsequent swaps.
      if (changed) untracked(() => this.focusFirstInActivePanel());
    });
  }

  private refreshActivePageHeightObserver(): void {

    const activePage = this.getActivePageElement();
    if (activePage === this.observedActivePage) {
      if (activePage) this.updateActivePageHeight(activePage);
      return;
    }

    this.activePageResizeObserver?.disconnect();
    this.observedActivePage = activePage;

    if (!activePage) {
      this.$activePageHeight.set(null);
      return;
    }

    const ResizeObserverCtor = this.document.defaultView?.ResizeObserver;
    if (ResizeObserverCtor) {
      this.activePageResizeObserver = new ResizeObserverCtor(() => this.updateActivePageHeight(activePage));
      this.activePageResizeObserver.observe(activePage);
    }

    this.updateActivePageHeight(activePage);
  }

  private getActivePageElement(): HTMLElement | null {

    const root = this.elementRef.nativeElement as HTMLElement;
    const track = root.querySelector<HTMLElement>('.settings-panel-track');
    const pages = track?.querySelectorAll<HTMLElement>('settings-panel-page');
    return pages?.[this.$panelIndex()] ?? null;
  }

  private updateActivePageHeight(activePage: HTMLElement): void {

    const contentHeight = this.getDesiredPageHeight(activePage);
    const availableHeight = this.getAvailablePanelHeight();
    const nextHeight = Math.min(contentHeight, availableHeight);
    if (nextHeight <= 0 || this.$activePageHeight() === nextHeight) return;
    this.$activePageHeight.set(nextHeight);
  }

  private getDesiredPageHeight(page: HTMLElement): number {

    const header = page.querySelector<HTMLElement>('.sp-page-header');
    const body = page.querySelector<HTMLElement>('.sp-page-body');
    const footer = page.querySelector<HTMLElement>('.sp-page-footer');
    const bodyStyles = body ? this.document.defaultView?.getComputedStyle(body) : null;
    const bodyPadding = bodyStyles ? parseFloat(bodyStyles.paddingTop) + parseFloat(bodyStyles.paddingBottom) : 0;

    return Math.ceil((header?.offsetHeight ?? 0) + bodyPadding + this.getDesiredBodyContentHeight(body) + (footer?.offsetHeight ?? 0));
  }

  private getDesiredBodyContentHeight(body: HTMLElement | null): number {

    if (!body) return 0;

    const picker = body.querySelector<HTMLElement>('settings-panel-picker');
    if (picker) return this.getDesiredPickerHeight(picker);

    const menuList = body.querySelector<HTMLElement>('.settings-menu-list');
    if (menuList) return Math.max(body.scrollHeight, menuList.scrollHeight);

    return body.scrollHeight;
  }

  private getDesiredPickerHeight(picker: HTMLElement): number {

    const search = picker.querySelector<HTMLElement>('.sp-picker-search');
    const listRoot = picker.querySelector<HTMLElement>('.sp-picker-list-root');
    const styles = this.document.defaultView?.getComputedStyle(picker);
    const gap = styles ? parseFloat(styles.gap) : 0;
    const visibleSections = [search, listRoot].filter((section): section is HTMLElement => !!section);

    return Math.ceil(this.getOuterHeight(search) + this.getScrollOuterHeight(listRoot) + Math.max(0, visibleSections.length - 1) * gap);
  }

  private getOuterHeight(element: HTMLElement | null): number {

    if (!element) return 0;

    const styles = this.document.defaultView?.getComputedStyle(element);
    return element.offsetHeight + this.getVerticalMargins(styles);
  }

  private getScrollOuterHeight(element: HTMLElement | null): number {

    if (!element) return 0;

    const styles = this.document.defaultView?.getComputedStyle(element);
    return element.scrollHeight + this.getVerticalMargins(styles);
  }

  private getVerticalMargins(styles: CSSStyleDeclaration | undefined): number {

    if (!styles) return 0;
    return parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
  }

  private getAvailablePanelHeight(): number {

    const host = this.elementRef.nativeElement as HTMLElement;
    const card = host.closest<HTMLElement>('.command-palette-card');
    if (!card) return Number.POSITIVE_INFINITY;

    const maxHeight = parseFloat(this.document.defaultView?.getComputedStyle(card).maxHeight ?? '');
    if (!Number.isFinite(maxHeight)) return Number.POSITIVE_INFINITY;

    const hostTop = host.getBoundingClientRect().top - card.getBoundingClientRect().top;
    return Math.max(0, Math.floor(maxHeight - hostTop));
  }

  protected resolveEntryInjector(entry: SettingsPanelComponentEntry): Injector | undefined {

    if (!entry.providers?.length) return undefined;

    const cached = this.entryInjectors.get(entry);
    if (cached) return cached;

    const created = Injector.create({ providers: entry.providers, parent: this.injector });
    this.entryInjectors.set(entry, created);
    return created;
  }

  protected $rendererMap = computed(() => {

    const map = new Map<string, TemplateRef<unknown>>();
    for (const dir of this.rendererDirectives()) {
      map.set(dir.$type(), dir.template);
    }
    return map;
  });

  // ── Navigation state ────────────────────────────────────────────────

  private $titleRegistry = signal<Map<string, string>>(new Map());
  protected $navigationStack = signal<string[]>([]);

  protected $panelIndex = computed(() => this.$navigationStack().length);

  protected $stackTitles = computed<string[]>(() => {

    const stack = this.$navigationStack();
    const registry = this.$titleRegistry();
    const items = this.$config().items;

    return stack.map(panelId => {

      const registryTitle = registry.get(panelId);
      if (registryTitle) return registryTitle;

      const item = items?.find(i => i.type !== 'divider' && i.type !== 'compound' && i.id === panelId);
      return (item && 'label' in item) ? item.label : panelId;
    });
  });

  protected $activePanels = computed<(SettingsPanelEntry | null)[]>(() => {

    const stack = this.$navigationStack();
    if (stack.length === 0) return [];

    const configRegistry = this.$config()?.registry;
    return stack.map(panelId => configRegistry?.get(panelId) ?? null);
  });

  protected $activeFooterLeftCommands = computed<ICommand[]>(() => {

    const panels = this.$activePanels();
    if (panels.length === 0) return this.$config().footerLeftCommands ?? [];
    return panels[panels.length - 1]?.footerLeftCommands ?? [];
  });

  protected $activeFooterRightCommands = computed<ICommand[]>(() => {

    const panels = this.$activePanels();
    if (panels.length === 0) return this.$config().footerRightCommands ?? [];
    return panels[panels.length - 1]?.footerRightCommands ?? [];
  });

  protected $activeFooterSplitButton = computed<SettingsPanelSplitButton | undefined>(() => {

    const panels = this.$activePanels();
    const splitButton = panels.length === 0 ? this.$config().footerSplitButton : panels[panels.length - 1]?.footerSplitButton;
    return this.withFormValidity(splitButton);
  });

  // Only reflect actual invalidity in the action's disabled state — during brief
  // async-validator pending intervals (250ms debounce) we keep the previous state
  // to avoid disabled-flicker on every form value change. The action handlers
  // re-check `valid()` before firing so a pending-then-invalid race is harmless.
  private $formActionDisabled = computed(() => !this.$panel().valid() && !this.$panel().pending());

  // ── Navigation API ──────────────────────────────────────────────────

  // Stack of trigger elements aligned with $navigationStack — restored on back.
  private readonly _triggerStack: HTMLElement[] = [];

  public navigateTo(panelId: string, options?: SettingsPanelNavigateOptions) {

    if (options?.title) {
      this.$titleRegistry.update(map => new Map(map).set(panelId, options.title!));
    }
    // Remember the element that opened this subpanel so navigateBack can restore focus to it.
    const trigger = this.document.activeElement as HTMLElement | null;
    this._triggerStack.push(trigger ?? this.elementRef.nativeElement);
    this.$navigationStack.update(stack => [...stack, panelId]);
    this.focusFirstInActivePanel();
  }

  public navigateBack() {

    const trigger = this._triggerStack.pop();
    const stack = this.$navigationStack();
    const popped = stack[stack.length - 1] ?? null;
    this.$navigationStack.update(s => s.slice(0, -1));

    // Command-palette mode is single-shot — backing out of a picker without applying
    // should discard whatever was tentatively staged so the next picker drill-in starts clean.
    if (popped && this.$isCommandPaletteMode()) {
      this.resetPanelValue(popped);
    }

    if (trigger && this.elementRef.nativeElement.contains(trigger)) {
      // Restore focus to whatever opened this subpanel after the slide animation.
      setTimeout(() => trigger.focus(), 250);
    } else {
      this.focusFirstInActivePanel();
    }
  }

  private resetPanelValue(panelId: string): void {

    const form = this.$panel();
    const control = form.getControl(panelId);
    if (!control) return;

    // Use the array-vs-string heuristic the builder uses (multi pickers initialize to []).
    const empty = Array.isArray(control.value) ? [] : '';
    control.setValue(empty);
    control.markAsPristine();
  }

  public navigateToRoot() {

    this._triggerStack.length = 0;
    this.$navigationStack.set([]);
    this.focusFirstInActivePanel();
  }

  private _closing = false;

  protected onEscape(event: KeyboardEvent): void {

    event.stopPropagation();
    if (this.$navigationStack().length > 0) {
      this.navigateBack();
      return;
    }
    if (this._closing) return;  // guard against confirm-dialog Esc bubbling back
    this._closing = true;
    void this.requestClose().finally(() => { this._closing = false; });
  }

  protected onBackspace(event: KeyboardEvent): void {

    const active = this.document.activeElement as HTMLElement | null;
    if (!active) return;
    const tag = active.tagName;

    // Don't hijack Backspace inside text-editing surfaces — UNLESS the input
    // is empty (typical "search field" case where users expect Backspace to
    // navigate back once there's nothing left to delete).
    if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable) {
      const value = (active as HTMLInputElement | HTMLTextAreaElement).value;
      const isEmpty = !value || value.length === 0;
      if (!isEmpty) return;
    }

    if (this.$navigationStack().length > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.navigateBack();
    }
  }

  /**
   * After Angular renders the new active page, focus its first focusable
   * element so keyboard users land in a sensible place.
   */
  private focusFirstInActivePanel(): void {

    setTimeout(() => {
      const stackIndex = this.$navigationStack().length;

      // Prefer an explicit `focus()` method on the active panel's component instance.
      const outlets = this.componentOutlets();
      const activeOutlet = outlets[stackIndex - 1];
      const instance = activeOutlet?.componentInstance as { focus?: () => void } | null | undefined;
      if (instance?.focus) { instance.focus(); return; }

      // Fallback: scan the active page DOM for the first focusable element.
      const root = this.elementRef.nativeElement as HTMLElement;
      const pages = root.querySelectorAll<HTMLElement>('settings-panel-page');
      const activePage = pages[stackIndex] ?? pages[0];
      if (!activePage) return;

      const body = activePage.querySelector<HTMLElement>('.sp-page-body') ?? activePage;
      // Find the first focusable; explicitly skip the picker's search input so
      // keyboard users land on the first picker item instead of the search box.
      const candidates = body.querySelectorAll<HTMLElement>(
        'input:not([disabled]):not([type="hidden"]), button:not([disabled]):not([tabindex="-1"]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusable = Array.from(candidates).find(el => !el.closest('.sp-picker-search'));
      focusable?.focus();
    }, 250);
  }

  // ── Form field updates ─────────────────────────────────────────────

  protected getComponentInputs(panel: SettingsPanelComponentEntry): Record<string, unknown> {

    return { formGroup: this.$formGroup(), control: panel.control, onFieldUpdate: (id: string, value: unknown) => this.onFieldChange(id, value), ...panel.inputs };
  }

  protected onFieldChange(fieldId: string, value: unknown) {

    const panel = this.$panel();
    panel.update(fieldId, value);
    this._onChange(panel.value());
    this._onTouched();
  }

  private setupValueChangeEmitter(): void {

    effect(() => {

      const panel = this.$panel();
      const val = panel.value();
      untracked(() => this.valueChange.emit(val as Record<string, unknown>));
    });
  }

  private withFormValidity(splitButton?: SettingsPanelSplitButton): SettingsPanelSplitButton | undefined {

    if (!splitButton) return undefined;

    const disabled = this.$formActionDisabled();

    return {
      command: splitButton.command.clone({
        disabled: splitButton.command.disabled || disabled,
      }),
      commands: splitButton.commands.map(command => command.clone({
        disabled: command.disabled || disabled,
      })),
    };
  }

  // ── Event handlers ──────────────────────────────────────────────────

  protected onMenuItemClicked(item: SettingsPanelMenuItem) {

    if (item.type === 'navigate') {
      const navItem = item as SettingsPanelNavigateItem;
      this.navigateTo(navItem.id, { title: navItem.label, ...navItem.navigateOptions });
    }

    if (item.type === 'entityPicker') {
      const epItem = item as SettingsPanelEntityPickerItem;
      this.navigateTo(epItem.id, { title: epItem.label });
    }

    this.itemClicked.emit(item);
  }


  public async requestClose(): Promise<boolean> {

    if (!(await this.canClose())) return false;

    this.closed.emit();
    return true;
  }

  private async canClose(): Promise<boolean> {

    if (!this.$confirmDiscardChanges() || !this.$panel().dirty()) return true;

    return this.confirmService.confirm(this.$discardChangesTitle(), this.$discardChangesMessage(), 'warning', 'Discard', 'Cancel');
  }

  protected onClose() {

    this.closed.emit();
  }

  // ── CVA ─────────────────────────────────────────────────────────────

  public writeValue(value: Record<string, unknown>): void {

    if (value) {
      this.$panel().setValues(value);
    }
  }

  public registerOnChange(fn: (value: unknown) => void): void {

    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this._onTouched = fn;
  }

  // ── Click outside ───────────────────────────────────────────────────

  private setupClickOutside(): void {

    let active = false;
    requestAnimationFrame(() => active = true);

    fromEvent<MouseEvent>(this.document, 'pointerdown').pipe(filter(() => active), takeUntilDestroyed(this.destroyRef)).subscribe(async event => {

      if (!this.$dismissOnClickOutside()) return;
      if (this.elementRef.nativeElement.contains(event.target as Node)) return;
      if ((event.target as HTMLElement)?.closest('.cdk-overlay-container')) return;

      const dismissEvent = new SettingsPanelDismissEvent();
      this.clickOutside.emit(dismissEvent);

      if (!dismissEvent.defaultPrevented) {
        await this.requestClose();
      }
    });
  }
}
